import React, { useState, useEffect, useRef } from 'react';
import { KTSVG } from '../../../../_mexablock/helpers';
import { useFormik } from 'formik';
import { getAllIndicatorAct } from '../../../service/activity/activity';
import Select from 'react-select';

type SelectOption = { value: string; label: string };

type Props = {
  initFilterValues?: any;
  onCleanFilter: () => void;
  onSearchFilter: (values: any) => void;
};

const comparisonOptions: SelectOption[] = [
  { value: 'month', label: 'Mes contra Mes' },
  { value: 'year', label: 'Año contra Año' },
];

const monthOptions: SelectOption[] = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

const Filter: React.FC<Props> = ({
  initFilterValues,
  onSearchFilter,
  onCleanFilter,
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const [filterValues, setFilterValues] = useState(initFilterValues || {});
  const [oldYear] = useState<number>(2025);
  const [yearOptions, setYearOptions] = useState<SelectOption[]>([]);

  const [comparisonType, setComparisonType] = useState<SelectOption | null>(
    null,
  );

  const [year1, setYear1] = useState<SelectOption | null>(null);
  const [year2, setYear2] = useState<SelectOption | null>(null);
  const [month1, setMonth1] = useState<SelectOption | null>(null);
  const [month2, setMonth2] = useState<SelectOption | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const hasComparisonSelected = comparisonType !== null;
  const isMonthComparison = comparisonType?.value === 'month';
  const isYearEnabled = hasComparisonSelected;
  const isMonthEnabled = hasComparisonSelected && isMonthComparison;

  // Filtrar meses disponibles según el año seleccionado
  const getAvailableMonths = (
    selectedYear: string | undefined,
  ): SelectOption[] => {
    if (!selectedYear) return monthOptions;
    const yearNum = parseInt(selectedYear, 10);
    // Si es el año actual, solo mostrar meses concluidos (anteriores al mes actual)
    if (yearNum === currentYear) {
      return monthOptions.filter((m) => parseInt(m.value, 10) < currentMonth);
    }
    return monthOptions;
  };

  // Opciones de mes para el primer selector
  const month1Options = getAvailableMonths(year1?.value);

  // Opciones de mes para el segundo selector (excluir mismo mes si es el mismo año)
  const month2Options = (() => {
    const available = getAvailableMonths(year2?.value);
    // Si ambos años son iguales, excluir el mes seleccionado en month1
    if (
      year1?.value &&
      year2?.value &&
      year1.value === year2.value &&
      month1?.value
    ) {
      return available.filter((m) => m.value !== month1.value);
    }
    return available;
  })();

  // Limpiar month1 si ya no es válido cuando cambia year1
  useEffect(() => {
    if (month1 && year1) {
      const isValid = month1Options.find((m) => m.value === month1.value);
      if (!isValid) {
        setMonth1(null);
      }
    }
  }, [year1?.value]);

  // Limpiar month2 si ya no es válido cuando cambian year1, year2 o month1
  useEffect(() => {
    if (month2 && year2) {
      const isValid = month2Options.find((m) => m.value === month2.value);
      if (!isValid) {
        setMonth2(null);
      }
    }
  }, [year1?.value, year2?.value, month1?.value]);

  useEffect(() => {
    if (initFilterValues) {
      setFilterValues(initFilterValues);
    }
  }, [initFilterValues]);

  const formFilter = useFormik({
    initialValues: filterValues,
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      if (!comparisonType) return;

      const filterData = {
        comparisonType: comparisonType.value,
        year_1: year1?.value,
        year_2: year2?.value,
        ...(isMonthComparison && {
          month1: month1?.value,
          month2: month2?.value,
        }),
      };
      onSearchFilter(filterData);
      closeMenu();
    },
  });

  useEffect(() => {
    if (oldYear) {
      const options: SelectOption[] = [];
      for (let year = oldYear; year <= currentYear; year++) {
        options.push({ value: year.toString(), label: year.toString() });
      }
      options.reverse();
      setYearOptions(options);
    }
  }, [oldYear]);

  const handleComparisonChange = (option: SelectOption | null) => {
    setComparisonType(option);
    setYear1(null);
    setYear2(null);
    setMonth1(null);
    setMonth2(null);
  };

  const handleYear1Change = (opt: SelectOption | null) => {
    setYear1(opt);
    // Limpiar month1 si el mes seleccionado ya no es válido
    if (month1 && opt) {
      const available = getAvailableMonths(opt.value);
      if (!available.find((m) => m.value === month1.value)) {
        setMonth1(null);
      }
    }
  };

  const handleYear2Change = (opt: SelectOption | null) => {
    setYear2(opt);
    // Limpiar month2 si el mes seleccionado ya no es válido
    if (month2 && opt) {
      const available = getAvailableMonths(opt.value);
      const isSameYear = year1?.value === opt.value;
      const isInvalidMonth =
        !available.find((m) => m.value === month2.value) ||
        (isSameYear && month1?.value === month2.value);
      if (isInvalidMonth) {
        setMonth2(null);
      }
    }
  };

  const handleMonth1Change = (opt: SelectOption | null) => {
    setMonth1(opt);
    // Si month2 es igual al nuevo month1 y es el mismo año, limpiar month2
    if (opt && year1?.value === year2?.value && month2?.value === opt.value) {
      setMonth2(null);
    }
  };

  const handleCleanFilter = () => {
    setComparisonType(null);
    setYear1(null);
    setYear2(null);
    setMonth1(null);
    setMonth2(null);
    formFilter.resetForm();
    onCleanFilter();
    closeMenu();
  };

  const closeMenu = () => {
    // Cerrar el menú usando el API de KT Menu
    if (menuRef.current) {
      const menuElement = menuRef.current.closest('[data-kt-menu="true"]');
      if (menuElement) {
        menuElement.classList.remove('show');
      }
    }
  };

  return (
    <>
      <div className="card-title align-items-start flex-column">
        <a
          href="#/"
          className={
            comparisonType !== null
              ? 'btn btn-sm btn-light-primary'
              : 'btn btn-sm btn-primary'
          }
          data-kt-menu-trigger="click"
          data-kt-menu-placement="left-start"
          data-kt-menu-flip="top-end"
        >
          Fecha
          <KTSVG
            path="/media/icons/duotune/general/gen014.svg"
            className="svg-icon-5 svg-icon-gray-500 ms-2"
          />
        </a>
        <div
          ref={menuRef}
          className="menu menu-sub menu-sub-dropdown w-250px w-md-400px"
          data-kt-menu="true"
          style={{
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'visible',
            position: 'absolute',
            inset: 'auto auto auto auto',
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="px-7 py-5">
            <div className="fs-5 text-dark fw-bolder">
              Opciones de comparación
            </div>
          </div>

          <div className="separator border-gray-200"></div>
          <form
            id="kt_modal_generic_form"
            className="form"
            action="#"
            onSubmit={formFilter.handleSubmit}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-7 py-5">
              <div className="mb-5">
                <Select
                  options={comparisonOptions}
                  placeholder="Seleccione"
                  onChange={handleComparisonChange}
                  value={comparisonType}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  noOptionsMessage={() => <span>No hay registros</span>}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#181c32',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#7E8199',
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      border: '1px solid #e4e6ef',
                      borderRadius: '6px',
                      padding: '3px',
                      fontSize: '1.1rem',
                      boxShadow: 'none',
                      backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                    }),
                    option: (provided) => ({ ...provided, fontSize: '1.1rem' }),
                  }}
                />
              </div>

              <div className="mb-5 row">
                <div className="col">
                  <Select
                    options={yearOptions}
                    placeholder="Año"
                    onChange={handleYear1Change}
                    value={year1}
                    isDisabled={!isYearEnabled}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    noOptionsMessage={() => <span>No hay registros</span>}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#181c32',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#7E8199',
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        border: '1px solid #e4e6ef',
                        borderRadius: '6px',
                        padding: '3px',
                        fontSize: '1.1rem',
                        boxShadow: 'none',
                        backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontSize: '1.1rem',
                      }),
                    }}
                  />
                </div>
                <div className="col">
                  <Select
                    options={yearOptions}
                    placeholder="Año"
                    onChange={handleYear2Change}
                    value={year2}
                    isDisabled={!isYearEnabled}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    noOptionsMessage={() => <span>No hay registros</span>}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#181c32',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#7E8199',
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        border: '1px solid #e4e6ef',
                        borderRadius: '6px',
                        padding: '3px',
                        fontSize: '1.1rem',
                        boxShadow: 'none',
                        backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontSize: '1.1rem',
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="mb-5 row">
                <div className="col">
                  <Select
                    options={month1Options}
                    placeholder="Mes"
                    onChange={handleMonth1Change}
                    value={month1}
                    isDisabled={!isMonthEnabled || !year1}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    noOptionsMessage={() => <span>No hay registros</span>}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#181c32',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#7E8199',
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        border: '1px solid #e4e6ef',
                        borderRadius: '6px',
                        padding: '3px',
                        fontSize: '1.1rem',
                        boxShadow: 'none',
                        backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontSize: '1.1rem',
                      }),
                    }}
                  />
                </div>
                <div className="col">
                  <Select
                    options={month2Options}
                    placeholder="Mes"
                    onChange={(opt) => setMonth2(opt)}
                    value={month2}
                    isDisabled={!isMonthEnabled || !year2}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    noOptionsMessage={() => <span>No hay registros</span>}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#181c32',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        color: '#7E8199',
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        border: '1px solid #e4e6ef',
                        borderRadius: '6px',
                        padding: '3px',
                        fontSize: '1.1rem',
                        boxShadow: 'none',
                        backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontSize: '1.1rem',
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="reset"
                  className="btn btn-sm btn-white btn-active-light-primary me-2"
                  data-kt-menu-dismiss="true"
                  onClick={handleCleanFilter}
                >
                  Limpiar
                </button>

                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  data-kt-menu-dismiss="true"
                >
                  Buscar
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export { Filter };
